import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EmeraldSwitch from "../../components/EmeraldSwitch";
import { listNotificationSubscribers, updateNotificationSubscriber } from "../../api/notificationPreferences";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";

const DEFAULT_TOPICS = [
  {
    id: "APPLICATION_NEW",
    labelKey: "notifications.admin.topics.APPLICATION_NEW.label",
    descriptionKey: "notifications.admin.topics.APPLICATION_NEW.description",
  },
  {
    id: "MUTATION_REQUEST",
    labelKey: "notifications.admin.topics.MUTATION_REQUEST.label",
    descriptionKey: "notifications.admin.topics.MUTATION_REQUEST.description",
  },
  {
    id: "FAMILY_NEW",
    labelKey: "notifications.admin.topics.FAMILY_NEW.label",
    descriptionKey: "notifications.admin.topics.FAMILY_NEW.description",
    defaultLabel: "New family registered",
    defaultDescription: "Subscribers receive an email when a new family is added, for example to prepare a welcome package.",
  },
];

const formatDate = (value, t) => {
  if (!value) return t("notifications.admin.never", { defaultValue: "No timestamp" });
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) throw new Error("Invalid date");
    return date.toLocaleString();
  } catch {
    return value;
  }
};

const resolveUserId = (entry) =>
  entry?.userId ||
  entry?.user?.id ||
  entry?.user?.uuid ||
  entry?.user_id ||
  entry?.userUuid ||
  entry?.parentId ||
  entry?.id ||
  entry?.uuid;

const resolveChannel = (entry) => entry?.channel || entry?.channelName || entry?.channel_name || "EMAIL";

const resolveEmail = (entry) =>
  entry?.user?.email || entry?.email || entry?.userEmail || entry?.user_email || entry?.user_mail || "";

const resolveDisplayName = (entry, t) => {
  const user = entry?.user || {};
  const email = resolveEmail(entry);
  // Prefer first/last name combinations
  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    [entry?.firstName, entry?.lastName].filter(Boolean).join(" ") ||
    user.fullName ||
    user.fullname ||
    entry?.fullName ||
    entry?.fullname ||
    user.displayName ||
    entry?.displayName ||
    entry?.userName;
  return fullName || email || resolveUserId(entry) || t?.("notifications.admin.unknown_user", { defaultValue: "Unknown user" }) || "Unknown user";
};

const matchesQuery = (entry, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const fields = [
    entry.username,
    entry.userName,
    entry.email,
    entry.firstName,
    entry.lastName,
    entry.displayName,
    `${entry.firstName || ""} ${entry.lastName || ""}`.trim(),
  ]
    .filter(Boolean)
    .map((value) => value.toString().toLowerCase());
  return fields.some((field) => field.includes(term));
};

function SubscriberAdder({ topic, onAdded, canManage, t, topicsConfig }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setSearching(true);
      setError("");
      try {
        let users = allUsers;
        if (!usersLoaded) {
          const { data } = await http.get("/admin/users");
          users = Array.isArray(data) ? data : [];
          if (!cancelled) {
            setAllUsers(users);
            setUsersLoaded(true);
          }
        }
        const filtered = (users || [])
          .filter((user) => matchesQuery(user, query))
          .slice(0, 8);
        if (!cancelled) setResults(filtered);
      } catch (err) {
        if (!cancelled) {
          const mapped = mapHttpErrorToMessage(err, t);
          setError(
            mapped?.message ||
              t("notifications.admin.search_error", {
                defaultValue: "Zoeken naar gebruikers is mislukt.",
              })
          );
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [allUsers, query, t, topic, topicsConfig, usersLoaded]);

  const handleSelect = (entry) => {
    setSelected(entry);
    const name = resolveDisplayName(entry, t);
    const email = resolveEmail(entry);
    setQuery(email && name && name !== email ? `${name} (${email})` : name || email || "");
    setResults([]);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canManage || !selected) return;
    const userId = resolveUserId(selected);
    if (!userId) {
      setError(
        t("notifications.admin.missing_user", {
          defaultValue: "Cannot update this preference because the user identifier is missing.",
        })
      );
      return;
    }
    setAdding(true);
    setError("");
    try {
      await updateNotificationSubscriber(userId, { topic, channel: resolveChannel(selected), enabled: true });
      setQuery("");
      setSelected(null);
      setResults([]);
      if (onAdded) await onAdded();
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setError(
        mapped?.message ||
          t("notifications.admin.add_error", {
            defaultValue: "Toevoegen van deze gebruiker is mislukt.",
          })
      );
    } finally {
      setAdding(false);
    }
  };

  const showDropdown = results.length > 0 && query.trim().length >= 2;

  return (
    <form onSubmit={handleAdd} className="relative space-y-2">
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
            {t("notifications.admin.add_user_label", { defaultValue: "Ouder toevoegen" })}
          </label>
          <div className="relative">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={!canManage}
              placeholder={t("notifications.admin.add_user_placeholder", {
                defaultValue: "Zoek op naam of e-mail",
              })}
              className="w-full rounded-xl border border-indigo-100/70 bg-white px-3 py-2 text-sm text-gray-900 shadow-inner placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100 dark:placeholder:text-gray-400"
            />
            {showDropdown && (
              <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-indigo-100/70 bg-white shadow-lg dark:border-indigo-500/40 dark:bg-gray-900/95">
                {results.map((entry) => {
                  const key = resolveUserId(entry) || entry?.parentId || entry?.email || entry?.uuid;
                  const name = resolveDisplayName(entry, t);
                  const email = resolveEmail(entry);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => handleSelect(entry)}
                        className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-indigo-50 focus:bg-indigo-50 dark:text-gray-100 dark:hover:bg-indigo-500/10"
                      >
                        <div className="flex-1">
                          <div className="font-semibold">{name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{email || "n/a"}</div>
                        </div>
                        <span className="text-[10px] uppercase text-indigo-600 dark:text-indigo-300">
                          {t("notifications.admin.select", { defaultValue: "Selecteer" })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {searching && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("notifications.admin.searching", { defaultValue: "Zoeken..." })}
            </p>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {t("notifications.admin.no_results", { defaultValue: "Geen resultaten." })}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canManage || !selected || adding}
          className="lumen-glow-sheen inline-flex items-center justify-center rounded-xl border border-indigo-200/70 bg-indigo-600/90 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {adding
            ? t("notifications.admin.adding_user", { defaultValue: "Toevoegen..." })
            : t("notifications.admin.add_user_button", { defaultValue: "Toevoegen" })}
        </button>
      </div>
      {error && (
        <div className="rounded border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}
    </form>
  );
}

export default function AdminNotificationSubscribers({ canManage = true, topicsConfig = DEFAULT_TOPICS }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [records, setRecords] = useState({});
  const [banner, setBanner] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [topicLoading, setTopicLoading] = useState(null);
  const topics = useMemo(
    () => topicsConfig.map((topic) => topic.id || topic.topic).filter(Boolean),
    [topicsConfig]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const entries = await Promise.all(
          topics.map(async (topic) => {
            const list = await listNotificationSubscribers(topic);
            return [topic, Array.isArray(list) ? list : []];
          })
        );
        if (!cancelled) {
          const mapped = Object.fromEntries(entries);
          setRecords(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          const mapped = mapHttpErrorToMessage(err, t);
          setError(
            mapped?.message ||
              t("notifications.admin.load_error", {
                defaultValue: "Failed to load notification subscribers.",
              })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t, topics]);
  useEffect(() => {
    if (!banner) return undefined;
    const timer = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(timer);
  }, [banner]);

  const loadTopic = useCallback(
    async (topic) => {
      setTopicLoading(topic);
      try {
        const list = await listNotificationSubscribers(topic);
        setRecords((prev) => ({
          ...prev,
          [topic]: Array.isArray(list) ? list : [],
        }));
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setError(
          mapped?.message ||
            t("notifications.admin.load_error", {
              defaultValue: "Failed to load notification subscribers.",
            })
        );
      } finally {
        setTopicLoading(null);
      }
    },
    [t]
  );

  const handleToggle = async (topic, entry, nextEnabled) => {
    if (!canManage) return;
    const userId = resolveUserId(entry);
    if (!userId) {
      setBanner({
        type: "error",
        message: t("notifications.admin.missing_user", {
          defaultValue: "Cannot update this preference because the user identifier is missing.",
        }),
      });
      return;
    }
    const channel = resolveChannel(entry);
    const key = `${userId}|${topic}|${channel}`;
    setSavingKey(key);
    try {
      await updateNotificationSubscriber(userId, {
        topic,
        channel,
        enabled: nextEnabled,
      });
      await loadTopic(topic);
      setBanner({
        type: "success",
        message: t("notifications.admin.toggle_success", {
          defaultValue: "Preference updated.",
        }),
      });
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setBanner({
        type: "error",
        message:
          mapped?.message ||
          t("notifications.admin.toggle_error", {
            defaultValue: "Updating this preference failed. Please try again.",
          }),
      });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t("notifications.admin.title", { defaultValue: "Abonnees per onderwerp" })}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          {t("notifications.admin.description", {
            defaultValue: "Bekijk wie zich heeft aangemeld voor elk notificatieonderwerp.",
          })}
        </p>
      </header>

      {error && (
        <div className="rounded border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}
      {banner && (
        <div
          className={`rounded border px-3 py-2 text-sm ${
            banner.type === "success"
              ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-emerald-400 dark:bg-emerald-500/10 dark:text-emerald-200"
              : "border-red-500 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200"
          }`}
        >
          {banner.message}
        </div>
      )}

      {topics.map((topic) => {
        const topicConfig = topicsConfig.find((t) => t.id === topic || t.topic === topic) || {};
        const { labelKey, descriptionKey } = topicConfig;
        const fallbackLabel =
          topicConfig.defaultLabel ||
          (topic === "APPLICATION_NEW"
            ? "New application submitted"
            : topic === "MUTATION_REQUEST"
            ? "New mutation request submitted"
            : topic || "Notification");
        const fallbackDescription =
          topicConfig.defaultDescription ||
          (topic === "APPLICATION_NEW"
            ? "Subscribers receive an email when a new application is submitted."
            : topic === "MUTATION_REQUEST"
            ? "Subscribers receive an email when a new mutation request is submitted."
            : "Subscribers receive updates for this topic.");
        const rows = records[topic] || [];
        const isTopicLoading = loading || topicLoading === topic;
        const sorted = rows
          .slice()
          .sort((a, b) => resolveDisplayName(a, t).localeCompare(resolveDisplayName(b, t)));
        return (
          <div
            key={topic}
            className="rounded-[28px] border border-indigo-100/60 bg-white/95 shadow-[0_20px_45px_rgba(15,23,42,0.12)] dark:border-indigo-500/40 dark:bg-gray-900/60 overflow-hidden"
          >
            <div className="px-5 py-4 bg-gradient-to-br from-indigo-50/85 via-white to-white shadow-inner dark:from-gray-900/40 dark:via-gray-900/30 dark:to-gray-900/40">
              <span className="mb-1 inline-flex items-center rounded-full bg-indigo-100 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200">
                {t("notifications.admin.topic_label", { defaultValue: "Onderwerp" })}
              </span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t(labelKey, {
                  defaultValue: fallbackLabel,
                })}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t(descriptionKey, {
                  defaultValue: fallbackDescription,
                })}
              </p>
            </div>

            <div className="px-5 pb-4">
              <SubscriberAdder
                topic={topic}
                canManage={canManage}
                t={t}
                topicsConfig={topicsConfig}
                onAdded={async () => {
                  await loadTopic(topic);
                  setBanner({
                    type: "success",
                    message: t("notifications.admin.add_success", {
                      defaultValue: "Ouder toegevoegd aan dit onderwerp.",
                    }),
                  });
                }}
              />
            </div>

            <div className="px-2 pb-4 pt-1">
              <LdsTableCard className="overflow-hidden">
                <LdsTableScroll>
                  <LdsTable>
                    <LdsTableHead>
                      <LdsTableRow>
                        <LdsTableHeadCell>{t("notifications.admin.columns.user", { defaultValue: "Ouder" })}</LdsTableHeadCell>
                        <LdsTableHeadCell>{t("notifications.admin.columns.email", { defaultValue: "E-mail" })}</LdsTableHeadCell>
                        <LdsTableHeadCell>{t("notifications.admin.columns.channel", { defaultValue: "Kanaal" })}</LdsTableHeadCell>
                        <LdsTableHeadCell>{t("notifications.admin.columns.enabled", { defaultValue: "Actief" })}</LdsTableHeadCell>
                      </LdsTableRow>
                    </LdsTableHead>
                    <LdsTableBody>
                  {sorted.length === 0 ? (
                    <LdsTableRow>
                      <LdsTableCell colSpan={4} className="text-gray-500 dark:text-gray-300">
                        {isTopicLoading
                          ? t("notifications.admin.loading", { defaultValue: "Loading..." })
                          : t("notifications.admin.empty", { defaultValue: "No subscribers yet." })}
                      </LdsTableCell>
                    </LdsTableRow>
                  ) : (
                    sorted.map((entry, index) => {
                      const email = resolveEmail(entry);
                      const displayName = resolveDisplayName(entry, t);
                      const channel = resolveChannel(entry);
                      const enabled = Boolean(entry?.enabled);
                      const key =
                        entry?.id ||
                        entry?.userId ||
                        email ||
                        `${topic}-${displayName}-${index}`;
                      const toggleKey = `${resolveUserId(entry)}|${topic}|${resolveChannel(entry)}`;
                      const pending = savingKey === toggleKey || topicLoading === topic;
                      return (
                        <LdsTableRow key={key}>
                          <LdsTableCell className="font-semibold text-slate-950 dark:text-white">{displayName}</LdsTableCell>
                          <LdsTableCell>
                            {email || t("notifications.admin.unknown_email", { defaultValue: "n.v.t." })}
                          </LdsTableCell>
                          <LdsTableCell>{channel}</LdsTableCell>
                          <LdsTableCell>
                            <div className="flex items-center gap-3">
                              <EmeraldSwitch
                                checked={enabled}
                                disabled={!canManage || pending || loading}
                                onChange={(value) => handleToggle(topic, entry, value)}
                              />
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  enabled
                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200"
                                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                }`}
                              >
                                {enabled
                                  ? t("notifications.admin.enabled_yes", { defaultValue: "Ja" })
                                  : t("notifications.admin.enabled_no", { defaultValue: "Nee" })}
                              </span>
                              {enabled && entry?.eligible === false && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                                  {t("notifications.admin.not_eligible", {
                                    defaultValue: "Ontvangt geen mail: mist het recht",
                                  })}
                                </span>
                              )}
                            </div>
                          </LdsTableCell>
                        </LdsTableRow>
                      );
                    })
                  )}
                    </LdsTableBody>
                  </LdsTable>
                </LdsTableScroll>
              </LdsTableCard>
            </div>
          </div>
        );
      })}
    </section>
  );
}
